import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  Fade,
  Grid,
  Typography,
} from "@material-ui/core";
import { IKImage } from "imagekitio-react";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import useTemplateFunctions from "../../../hooks/useTemplateFunctions";
import useTemplateStyles from "../../../hooks/useTemplateStyles";
import { PFXCardType } from "../../../models/Interfaces";
import { RootState } from "../../../redux/rootReducer";
import {
  currencyFormatter,
  selectCartIsOpen,
  selectIdenticalItemsInCart,
  selectCleverlynk,
} from "../../../redux/slices/cleverlynkSlice";
import { selectCurrency, selectItemById, selectSkuById } from "../../../redux/slices/templateSlice";

import { useTranslation } from "react-i18next";
import "../../../i18n/config";

interface IClassicCard {
  id: string;
  onClick: () => void;
  index: number;
}

const ClassicCard = ({ id, onClick, index }: IClassicCard) => {
  const { cardType, cardStyles } = useTemplateStyles();
  const { shouldHideSoldOutItem } = useTemplateFunctions();

  const [cardDimensions, setCardDimensions] = useState<number>(0);

  const { t } = useTranslation("common");

  const item = useSelector((state: RootState) => selectItemById(state, id));
  const itemSku = useSelector((state: RootState) => selectSkuById(state, item?.sku ?? ""));
  const identicalItemsInCart = useSelector(selectIdenticalItemsInCart);
  const currency = useSelector(selectCurrency);
  const cartIsOpen = useSelector(selectCartIsOpen);
  const cleverlynk = useSelector(selectCleverlynk);
  const initRef = useRef<boolean>(true);

  const cardWidth = React.useMemo(() => {
    const widthMultiplier = () => {
      if (initRef.current) {
        return 1;
      } else {
        initRef.current = false;
        return cartIsOpen ? 0.8 : 1.25;
      }
    };
    const width = cardDimensions * widthMultiplier();
    return width;
  }, [cartIsOpen, cardDimensions]);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      header: {
        padding: 0,
        position: "relative",
      },
      rootCard: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      },
      actionAreaCard: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      },
      mediaCard: {
        height: cardDimensions,
        width: "100%",
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
        position: "relative",
        "&.large": {
          display: "flex",
          width: cardType === PFXCardType.VERTICAL ? "inherit" : "auto",
          justifyContent: "center",
        },
      },
      contentCard: {
        textAlign: "center",
        width: "100%",
        margin: "auto",
        padding: "4px 0px",
      },
      actionsCard: {
        padding: 0,
      },
      mediaSoldOut: {
        width: "100%",
        height: "inherit",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        background: "rgba(0, 0, 0, 0.6)",
        position: "absolute",
        top: 0,
      },
      soldOut: {
        bottom: 0,
        color: "#FFFFFF",
        textAlign: "center",
        background: "rgba(200, 0, 0, 0.4)",
      },
      discount: {
        position: "absolute",
        top: "12%",
        left: "15%",
        width: "115%",
        transform: "rotate(45deg)",
        msTransform: "rotate(45deg)",
        WebkitTransform: "rotate(45deg)",
        color: "white",
        textAlign: "center",
        background: "#ff4b4b",
        padding: "3px 0px",
      },
      quantity: {
        position: "absolute",
        zIndex: 1,
        height: 21,
        width: 21,
        padding: 3,
        top: -10,
        right: -10,
        borderRadius: "50%",
        MozBorderRadius: "50%",
        WebkitBorderRadius: "50%",
        color: "#FFFFFF",
        textAlign: "center",
        background: "rgba(250, 70, 80, 1)",
      },
      priceWithDiscount: {
        "&.list": {
          textAlign: "end",
        },
        opacity: 0.7,
        MozOpacity: 0.7,
        textDecoration: "line-through",
        paddingRight: 8,
        textDecorationColor: "rgba(250, 0, 0, 1)",
      },
      button: {
        borderRadius: 0,
        MozBorderRadius: 0,
        WebkitBorderRadius: 0,
        "&.large": {
          borderRadius: 5,
          margin: "3px 0px",
          width: "40%",
        },
      },
      text: {
        lineHeight: 1.45,
        maxWidth: "95%",
        marginRight: "auto",
        overflow: "hidden",
        textOverflow: "ellipsis",
        msTextOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    })
  );

  const classes = useStyles();

  const stock = React.useMemo(() => {
    const itemStock = itemSku?.stock;
    const soldOut = item?.soldOut;

    if (soldOut) return 0;
    else if (itemStock === undefined) return Math.max() - 1;
    else return itemStock;
  }, [item, itemSku]);

  const shouldNotRender = React.useMemo(
    () => !item || item?.hidden || shouldHideSoldOutItem(item!),
    [item, shouldHideSoldOutItem]
  );

  const quantity = React.useMemo(() => {
    return (
      <Box className={classes.header}>
        {identicalItemsInCart[id] && identicalItemsInCart[id] > 0 && (
          <Box className={classes.quantity}>
            <Typography
              style={{ color: "#FFFFFF", fontSize: "0.875rem", fontWeight: "bold", width: "100%", textAlign: "center" }}
              variant="subtitle2"
              id={`quantity-item-${index}`}
              component="p">
              {identicalItemsInCart[id]}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }, [classes.header, classes.quantity, id, identicalItemsInCart]);

  const path =
    item?.images && item?.images.length > 0
      ? item?.images[0].split("cleverlynk-imgs.s3.amazonaws.com")[1]?.replace("resized-", "")
      : "";

  return shouldNotRender ? null : (
    <Fade in={true} timeout={400}>
      <Grid item xs={6} sm={4} md={3} lg={2}>
        <Box height="100%">
          {quantity}
          <Card
            style={cardStyles}
            className={`cardWidth ${classes.rootCard}`}
            elevation={5}
            ref={(e: any) => {
              if (e && e.offsetWidth !== cardDimensions && cardDimensions === 0) {
                return setCardDimensions(e.offsetWidth);
              } else return null;
            }}>
            <CardActionArea className={classes.actionAreaCard} disabled={stock === 0} onClick={onClick}>
              <div className={classes.mediaCard}>
                {cardWidth ? (
                  <IKImage
                    style={{
                      minWidth: cardWidth,
                      height: cartIsOpen ? "100%" : cardWidth,
                      position: "relative",
                    }}
                    path={path}
                    transformation={[
                      {
                        height: Math.max(400, cardWidth),
                        width: Math.max(400, cardWidth),
                      },
                    ]}
                    loading="lazy"
                    lqip={{ active: true, quality: 10, blur: 40 }}
                  />
                ) : (
                  <div style={{ height: 120, width: 120 }}></div>
                )}
                {!item?.decorative && stock === 0 ? (
                  <Box className={classes.mediaSoldOut}>
                    <Box py={1} className={classes.soldOut}>
                      <Typography style={{ fontSize: "1.25rem" }} variant="h6" component="p">
                        AGOTADO
                      </Typography>
                    </Box>
                  </Box>
                ) : !item?.decorative && item?.new ? (
                  <Grid container style={{ position: "absolute", top: 5, left: 5 }} justifyContent="flex-start">
                    <Chip label="Nuevo" size="small" color="secondary" />
                  </Grid>
                ) : null}
                {!item?.decorative &&
                  ((!!item?.discount && item?.discount?.type !== "none" && item?.priceWithDiscount! > 0) ||
                    (cleverlynk?.discount !== undefined &&
                      cleverlynk?.discount !== null &&
                      cleverlynk?.discount > 0)) && (
                    <Box className={classes.discount}>
                      <Typography style={{ fontSize: "0.75rem", color: "inherit" }} variant="subtitle2" component="p">
                        {item?.discount?.type === "percentage"
                          ? `${item?.discount?.amount}% Off`
                          : `-${currencyFormatter(item?.discount?.amount, currency)}`}
                      </Typography>
                    </Box>
                  )}
              </div>
              <CardContent
                className={classes.contentCard}
                style={{
                  backgroundColor: cardStyles?.cardColor,
                  maxWidth: cardWidth,
                }}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography
                      className={classes.text}
                      style={{ width: "100%" }}
                      variant="subtitle2"
                      component="h2"
                      id={`card-name-${index}`}>
                      {item?.name ?? ""}
                    </Typography>
                  </Grid>
                  {!item?.decorative && (
                    <Grid item xs={12}>
                      {item?.priceWithDiscount! > 0 && (
                        <Typography
                          className={classes.text}
                          variant="subtitle1"
                          component="p"
                          id={`card-price-${index}`}>
                          {item?.priceWithDiscount! > 0 && !!item?.discount && item?.discount?.type !== "none" && (
                            <Typography
                              className={`${classes.priceWithDiscount} ${classes.text}`}
                              variant="caption"
                              component="span">
                              {currencyFormatter(item?.price ?? 0, currency)}
                            </Typography>
                          )}
                          {currencyFormatter(item?.priceWithDiscount ?? 0, currency)}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </CardActionArea>
            {!item?.decorative && (
              <CardActions className={classes.actionsCard}>
                <Button
                  className={classes.button}
                  disabled={stock === 0}
                  fullWidth
                  variant="contained"
                  size="large"
                  id={`card-add-${index}`}
                  onClick={onClick}>
                  {t("add")}
                </Button>
              </CardActions>
            )}
          </Card>
        </Box>
      </Grid>
    </Fade>
  );
};
export default ClassicCard;
